import { type FC, useMemo } from "react";

import { PageReleaseForm } from "components/forms/PageReleaseForm";
import { PageType } from "components/server-types";
import { relativeDayAndTimeFormat } from "i18n/helpers";
import { t } from "i18n/i18n";
import { Chip, ChipProps } from "mds/components/Chip";
import { IconText } from "mds/components/IconText";
import { Popover, PopoverButton, PopoverPanel } from "mds/components/Popover";
import { ChevronDownIcon, ClockIcon } from "mds/icons";

type PageReleaseButtonProps = {
  page: PageType;
};

const ChipButton: FC<Omit<ChipProps<"button">, "as">> = (props) => <Chip as="button" {...props} />;

export const PageReleaseButton: FC<PageReleaseButtonProps> = ({ page }) => {
  const isReleased = Boolean(page.released_at);
  const scheduledReleaseAt = useMemo(
    () => (page.scheduled_release_at ? new Date(page.scheduled_release_at) : undefined),
    [page.scheduled_release_at],
  );

  if (isReleased) {
    return <Chip kind="gray">{t("page_release.released")}</Chip>;
  }

  const dateText = scheduledReleaseAt
    ? relativeDayAndTimeFormat(scheduledReleaseAt)
    : t("page_release.schedule");

  return (
    <Popover className="relative">
      <PopoverButton as={ChipButton} kind="white" title={dateText}>
        <IconText
          iconEnd={<ChevronDownIcon />}
          iconStart={scheduledReleaseAt ? <ClockIcon /> : undefined}
          text={dateText}
        />
      </PopoverButton>

      <PopoverPanel anchor="bottom" className="!z-[200]">
        {({ close }) => (
          <PageReleaseForm close={close} date={scheduledReleaseAt} pageId={page.id} />
        )}
      </PopoverPanel>
    </Popover>
  );
};
